import { useState } from "react";
import {
  Activity,
  Eye,
  Globe,
  Link2,
  Loader2,
  Mail,
  RefreshCw,
  Repeat,
  Search,
  Share2,
  ShoppingCart,
  Trash2,
  Users
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";

const fmt = (n) => Number(n ?? 0).toLocaleString();

const PATH_LABELS = {
  "/": "Landing",
  "/read": "Opening excerpt",
  "/clearance": "Null Six clearance",
  "/personnel": "Personnel file"
};

const KIND_META = {
  search: { label: "Search", icon: Search },
  social: { label: "Social", icon: Share2 },
  retail: { label: "Retail", icon: ShoppingCart },
  email: { label: "Email", icon: Mail },
  referral: { label: "Referral", icon: Link2 },
  internal: { label: "On-site", icon: Globe },
  direct: { label: "Direct", icon: Globe }
};

const kindMeta = (kind) => KIND_META[kind] || KIND_META.referral;

const StatCard = ({ label, value, hint, icon: Icon, testId }) => (
  <div className="panel p-4">
    <div className="flex items-center justify-between gap-2">
      <p className="eyebrow">{label}</p>
      <Icon className="h-3.5 w-3.5 text-[hsl(174_62%_60%)]" aria-hidden="true" />
    </div>
    <p
      data-testid={testId}
      className="mt-1 font-mono text-3xl tabular-nums text-foreground"
    >
      {value}
    </p>
    {hint && (
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)]">
        {hint}
      </p>
    )}
  </div>
);

export const TrafficPanel = ({ stats, loading, onRefresh, onReset }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (loading && !stats) {
    return (
      <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="traffic-loading">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="panel h-24 animate-pulse bg-[hsl(220_18%_8%)] p-4" />
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <p
        data-testid="traffic-error"
        className="panel mt-6 px-4 py-10 text-center text-sm text-muted-foreground"
      >
        Telemetry unavailable.{" "}
        <button
          type="button"
          onClick={onRefresh}
          className="font-mono text-[12px] uppercase tracking-[0.14em] text-[hsl(174_62%_64%)] hover:underline"
        >
          Retry
        </button>
      </p>
    );
  }

  const daily = stats.daily || [];
  const paths = stats.top_paths || [];
  const sources = stats.top_sources || [];
  const channels = stats.channels || [];
  const peak = daily.reduce((m, d) => Math.max(m, d.views || 0), 0) || 1;
  const pathPeak = paths.reduce((m, p) => Math.max(m, p.views || 0), 0) || 1;
  const sourcePeak = sources.reduce((m, s) => Math.max(m, s.views || 0), 0) || 1;
  const excerpt = stats.excerpt || {
    openers: 0,
    openers_today: 0,
    prologue_finished: 0,
    quarter: 0,
    half: 0,
    three_quarter: 0,
    finished: 0,
    avg_depth: 0,
    prologue_rate: 0,
    finish_rate: 0
  };
  const noData = stats.total_views === 0;

  return (
    <div className="mt-6" data-testid="traffic-panel">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label="Unique visitors"
          value={fmt(stats.unique_visitors)}
          hint="All time"
          icon={Users}
          testId="traffic-unique-visitors"
        />
        <StatCard
          label="Total views"
          value={fmt(stats.total_views)}
          hint="All time"
          icon={Eye}
          testId="traffic-total-views"
        />
        <StatCard
          label="Views / visitor"
          value={fmt(stats.views_per_visitor)}
          hint="Engagement depth"
          icon={Activity}
          testId="traffic-views-per-visitor"
        />
        <StatCard
          label="Unique today"
          value={fmt(stats.unique_today)}
          hint="UTC day"
          icon={Users}
          testId="traffic-unique-today"
        />
        <StatCard
          label="Views today"
          value={fmt(stats.views_today)}
          hint="UTC day"
          icon={Eye}
          testId="traffic-views-today"
        />
        <StatCard
          label="Returning"
          value={fmt(stats.returning_visitors)}
          hint="More than one view"
          icon={Repeat}
          testId="traffic-returning-visitors"
        />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onRefresh}
          data-testid="traffic-refresh-button"
          className="inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border border-border px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground transition-colors duration-150 hover:text-foreground"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh telemetry
        </button>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              data-testid="traffic-reset-button"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border border-border px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground transition-colors duration-150 hover:border-[hsl(2_72%_46%/0.5)] hover:text-[hsl(2_80%_68%)]"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Purge counters
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent
            data-testid="traffic-reset-dialog"
            className="border-border bg-[hsl(220_18%_7%)]"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="font-mono text-sm uppercase tracking-[0.16em]">
                Purge visitor counters
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-muted-foreground">
                Every recorded view and unique visitor will be erased and
                counting restarts from zero. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid="traffic-reset-cancel"
                className="font-mono text-[11px] uppercase tracking-[0.14em]"
              >
                Abort
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="traffic-reset-confirm"
                onClick={() => {
                  setConfirmOpen(false);
                  onReset();
                }}
                className="bg-[hsl(2_72%_46%)] font-mono text-[11px] uppercase tracking-[0.14em] text-white hover:bg-[hsl(2_72%_40%)]"
              >
                Purge
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="panel mt-6 p-4 sm:p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="eyebrow">Daily traffic · last 14 days</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)]">
            Views / uniques
          </p>
        </div>

        {daily.length === 0 ? (
          <p
            data-testid="traffic-daily-empty"
            className="py-8 text-center text-sm text-muted-foreground"
          >
            No traffic recorded yet.
          </p>
        ) : (
          <ul className="mt-4 space-y-2" data-testid="traffic-daily-list">
            {daily.map((d) => (
              <li key={d.day} className="grid grid-cols-[4.5rem_1fr_4.5rem] items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                  {d.day.slice(5)}
                </span>
                <span className="h-2.5 overflow-hidden rounded-full bg-[hsl(220_18%_12%)]">
                  <span
                    className="block h-full rounded-full bg-[hsl(174_62%_44%)]"
                    style={{ width: `${Math.max(4, (d.views / peak) * 100)}%` }}
                  />
                </span>
                <span className="text-right font-mono text-[11px] tabular-nums text-foreground">
                  {fmt(d.views)}
                  <span className="text-muted-foreground"> / {fmt(d.visitors)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="panel mt-6 p-4 sm:p-5" data-testid="traffic-excerpt">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="eyebrow">
            Excerpt readership · &ldquo;Six Seconds&rdquo; + Chapter One
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)]">
            {fmt(excerpt.openers_today)} today
          </p>
        </div>

        {excerpt.openers === 0 ? (
          <p
            data-testid="traffic-excerpt-empty"
            className="py-8 text-center text-sm text-muted-foreground"
          >
            No one has opened the excerpt yet.
          </p>
        ) : (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div>
                <p className="eyebrow">Opened the excerpt</p>
                <p
                  data-testid="excerpt-openers"
                  className="mt-1 font-mono text-3xl tabular-nums"
                >
                  {fmt(excerpt.openers)}
                </p>
              </div>
              <div>
                <p className="eyebrow">Finished &ldquo;Six Seconds&rdquo;</p>
                <p
                  data-testid="excerpt-prologue-finished"
                  className="mt-1 font-mono text-3xl tabular-nums text-[hsl(32_80%_66%)]"
                >
                  {fmt(excerpt.prologue_finished)}
                  <span className="ml-2 text-sm text-muted-foreground">
                    {excerpt.prologue_rate}%
                  </span>
                </p>
              </div>
              <div>
                <p className="eyebrow">Read to the end</p>
                <p
                  data-testid="excerpt-finished"
                  className="mt-1 font-mono text-3xl tabular-nums text-[hsl(174_62%_66%)]"
                >
                  {fmt(excerpt.finished)}
                  <span className="ml-2 text-sm text-muted-foreground">
                    {excerpt.finish_rate}%
                  </span>
                </p>
              </div>
            </div>

            <ul className="mt-5 space-y-2" data-testid="excerpt-depth-list">
              {[
                ["Opened", excerpt.openers],
                ["Past 25%", excerpt.quarter],
                ["Halfway", excerpt.half],
                ["Past 75%", excerpt.three_quarter],
                ["Reached the end", excerpt.finished]
              ].map(([label, count]) => (
                <li
                  key={label}
                  className="grid grid-cols-[7.5rem_1fr_2.5rem] items-center gap-3"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                    {label}
                  </span>
                  <span className="h-2.5 overflow-hidden rounded-full bg-[hsl(220_18%_12%)]">
                    <span
                      className="block h-full rounded-full bg-[hsl(174_62%_44%)]"
                      style={{
                        width: `${Math.max(
                          2,
                          (count / (excerpt.openers || 1)) * 100
                        )}%`
                      }}
                    />
                  </span>
                  <span className="text-right font-mono text-[11px] tabular-nums">
                    {fmt(count)}
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)]">
              Average depth reached: {excerpt.avg_depth}%
            </p>
          </>
        )}
      </div>

      <div className="panel mt-3 overflow-hidden" data-testid="traffic-sources">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border bg-[hsl(220_18%_7%)] px-4 py-2.5">
          <span className="eyebrow">Visitor sources</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)]">
            Views / uniques
          </span>
        </div>

        {channels.length > 0 && (
          <div
            className="flex flex-wrap gap-2 border-b border-border px-4 py-3"
            data-testid="traffic-channel-chips"
          >
            {channels.map((c) => {
              const { label, icon: Icon } = kindMeta(c.kind);
              return (
                <span
                  key={c.kind}
                  data-testid={`traffic-channel-${c.kind}`}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-[hsl(220_18%_8%)] px-3 py-1.5"
                >
                  <Icon
                    className="h-3 w-3 text-[hsl(174_62%_60%)]"
                    aria-hidden="true"
                  />
                  <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    {label}
                  </span>
                  <span className="font-mono text-[11px] tabular-nums text-foreground">
                    {fmt(c.visitors)}
                  </span>
                </span>
              );
            })}
          </div>
        )}

        {sources.length === 0 ? (
          <p
            data-testid="traffic-sources-empty"
            className="px-4 py-10 text-center text-sm text-muted-foreground"
          >
            No referral data yet. Sources appear once visitors arrive from
            Google, Amazon, social links or email.
          </p>
        ) : (
          <ul data-testid="traffic-sources-list">
            {sources.map((s) => {
              const { label, icon: Icon } = kindMeta(s.kind);
              return (
                <li
                  key={`${s.kind}:${s.source}`}
                  className="border-b border-border px-4 py-3 last:border-b-0"
                >
                  <div className="grid grid-cols-[1fr_3.5rem_3.5rem] items-baseline gap-3">
                    <span className="flex min-w-0 items-baseline gap-2">
                      <Icon
                        className="h-3.5 w-3.5 shrink-0 translate-y-0.5 text-[hsl(174_62%_58%)]"
                        aria-hidden="true"
                      />
                      <span className="truncate text-sm text-foreground">
                        {s.source}
                      </span>
                      <span className="hidden shrink-0 font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)] sm:inline">
                        {label}
                      </span>
                    </span>
                    <span className="text-right font-mono text-[12px] tabular-nums">
                      {fmt(s.views)}
                    </span>
                    <span className="text-right font-mono text-[12px] tabular-nums text-muted-foreground">
                      {fmt(s.visitors)}
                    </span>
                  </div>
                  <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-[hsl(220_18%_12%)]">
                    <span
                      className="block h-full rounded-full bg-[hsl(32_74%_52%)]"
                      style={{
                        width: `${Math.max(4, (s.views / sourcePeak) * 100)}%`
                      }}
                    />
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="panel mt-3 overflow-hidden">
        <div className="grid grid-cols-[1fr_4rem_4rem] gap-3 border-b border-border bg-[hsl(220_18%_7%)] px-4 py-2.5">
          <span className="eyebrow">Page</span>
          <span className="eyebrow text-right">Views</span>
          <span className="eyebrow text-right">Uniques</span>
        </div>

        {paths.length === 0 ? (
          <p
            data-testid="traffic-paths-empty"
            className="px-4 py-10 text-center text-sm text-muted-foreground"
          >
            No page data yet.
          </p>
        ) : (
          <ul data-testid="traffic-paths-list">
            {paths.map((p) => (
              <li
                key={p.path}
                className="border-b border-border px-4 py-3 last:border-b-0"
              >
                <div className="grid grid-cols-[1fr_4rem_4rem] items-baseline gap-3">
                  <span className="truncate">
                    <span className="text-sm text-foreground">
                      {PATH_LABELS[p.path] || p.path}
                    </span>
                    <span className="ml-2 font-mono text-[10px] text-[hsl(215_8%_45%)]">
                      {p.path}
                    </span>
                  </span>
                  <span className="text-right font-mono text-[12px] tabular-nums">
                    {fmt(p.views)}
                  </span>
                  <span className="text-right font-mono text-[12px] tabular-nums text-muted-foreground">
                    {fmt(p.visitors)}
                  </span>
                </div>
                <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-[hsl(220_18%_12%)]">
                  <span
                    className="block h-full rounded-full bg-[hsl(174_62%_38%)]"
                    style={{ width: `${Math.max(4, (p.views / pathPeak) * 100)}%` }}
                  />
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)]">
        {noData
          ? "Counters armed. Admin visits and known crawlers are never counted."
          : "Privacy-first: visitors are salted hashes. No IP addresses stored. Admin visits and crawlers excluded."}
      </p>
    </div>
  );
};

export default TrafficPanel;
